import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { Menu, X, User, LogOut, Shield, Search } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import bandflowLogo from "@/assets/bandflow-logo.png";
import bandflowAvatar from "@/assets/bandflow-avatar.png";

export function Header() {
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdmin();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center justify-between">
        <Link to="/" className="flex items-center min-w-[44px] min-h-[44px]">
          {/* Wordmark on all sizes */}
          <img
            src={bandflowLogo}
            alt="Bandflow"
            className="hidden md:block h-7 w-auto"
            width={129}
            height={28}
          />
          <img
            src={bandflowLogo}
            alt="Bandflow"
            className="block md:hidden h-6 w-auto"
            width={111}
            height={24}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 md:flex">
          <Link to="/" className="text-muted-foreground transition-colors hover:text-foreground" aria-label="Buscar">
            <Search className="h-5 w-5" />
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <User className="h-4 w-4" />
                  Mi cuenta
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/mi-cuenta">Mi cuenta</Link>
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/admin/venues">
                        <Shield className="mr-2 h-4 w-4" />
                        Ir al panel de administración
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm" variant="outline">
              <Link to="/auth">Acceder</Link>
            </Button>
          )}
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden min-w-[44px] min-h-[44px] flex items-center justify-center"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="border-t bg-background p-4 md:hidden animate-fade-in">
          <nav className="flex flex-col gap-3">
            <Link to="/" onClick={() => setMobileOpen(false)} className="text-sm font-medium">
              Buscar Salas
            </Link>
            {user ? (
              <>
                <Link to="/mi-cuenta" onClick={() => setMobileOpen(false)} className="text-sm font-medium">
                  Mi cuenta
                </Link>
                {isAdmin && (
                  <Link to="/admin/venues" onClick={() => setMobileOpen(false)} className="text-sm font-medium flex items-center gap-1">
                    <Shield className="h-3.5 w-3.5" /> Ir al panel de administración
                  </Link>
                )}
                <button
                  onClick={() => { signOut(); setMobileOpen(false); }}
                  className="text-left text-sm font-medium text-destructive"
                >
                  Cerrar sesión
                </button>
              </>
            ) : (
              <Link to="/auth" onClick={() => setMobileOpen(false)} className="text-sm font-medium">
                Acceder
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
